import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAcademy } from "@/contexts/AcademyContext";
import {
  Plus, Trash, ListChecks, PencilSimple, X, Copy, CaretUp, CaretDown, ArrowsClockwise, NotePencil,
  UsersThree, ArrowRight, Star, Share, DownloadSimple, Sparkle, Books, Stack, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { SessionBuilder, WeeklyLoadBar, FavoritesPalette, LibraryPalette, MesocycleFinisherDialog } from "./ProgramSessionBuilder";

const EMPTY_EX = {
  name: "", sets: 3, reps: "10", rest_sec: 90, notes: "",
  weight_kg: "", percentage_1rm: "", rpe: "", rir: "", tempo: "",
  velocity_target: "", distance_m: "", duration_sec: "", hr_zone: "",
  video_url: "", alternatives: "", regressions: "",
};
const newDay = (week, day) => ({ week, day, name: "", session_type: "strength", exercises: [{ ...EMPTY_EX }], is_rest: false });

const PROGRAM_TYPES = [
  { id: "annual", label: "Annual Plan" },
  { id: "mesocycle", label: "Mesocycle (3–6 weeks)" },
  { id: "microcycle", label: "Microcycle / Week" },
  { id: "session", label: "Single Session" },
];

const SESSION_TYPES = [
  { id: "strength", label: "Strength" },
  { id: "power", label: "Power" },
  { id: "speed", label: "Speed" },
  { id: "conditioning", label: "Conditioning" },
  { id: "mobility", label: "Mobility" },
  { id: "recovery", label: "Recovery" },
  { id: "taper", label: "Competition Taper" },
  { id: "rtp", label: "Return-to-Play" },
];

export default function CoachPrograms() {
  const navigate = useNavigate();
  const { role: academyRole, academy } = useAcademy();
  const [programs, setPrograms] = useState([]);
  const [clients, setClients] = useState([]);
  const [teams, setTeams] = useState([]);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [copyingProgram, setCopyingProgram] = useState(null);
  const [sharingProgram, setSharingProgram] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [filterMode, setFilterMode] = useState("all"); // all | manual | ai | imported
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState("recent"); // recent | assigned | az

  // Academy admins manage the org, not the training programs — bounce direct-URL access to /academy.
  // Sidebar and dashboard already hide the link; this guards paste-in access.
  useEffect(() => {
    if (academy && academyRole === "admin") {
      navigate("/academy", { replace: true });
    }
  }, [academy, academyRole, navigate]);

  const isAiProgram = (p) => (p.tags || []).includes("ai_generated");
  const isImported = (p) => !!p.imported_from || (p.tags || []).includes("imported");
  const isManual = (p) => !isAiProgram(p) && !isImported(p);

  const filteredPrograms = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = programs.filter((p) => {
      if (filterMode === "ai" && !isAiProgram(p)) return false;
      if (filterMode === "imported" && !isImported(p)) return false;
      if (filterMode === "manual" && !isManual(p)) return false;
      if (!q) return true;
      const hay = `${p.name || ""} ${p.description || ""} ${(p.tags || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
    // Apply sort — mutating a fresh copy is safe here
    list = list.slice();
    if (sortMode === "az") {
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortMode === "assigned") {
      list.sort((a, b) => (b.assignment_count || 0) - (a.assignment_count || 0));
    } else { // recent
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return list;
  }, [programs, filterMode, search, sortMode]);

  const filterCounts = {
    all: programs.length,
    manual: programs.filter(isManual).length,
    ai: programs.filter(isAiProgram).length,
    imported: programs.filter(isImported).length,
  };

  const load = () => api.get("/coach/programs").then(({ data }) => setPrograms(data));
  useEffect(() => {
    load();
    api.get("/coach/clients").then(({ data }) => setClients(data));
    api.get("/coach/teams").then(({ data }) => setTeams(data || [])).catch(() => {});
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    await api.delete(`/coach/programs/${id}`);
    toast.success("Deleted");
    load();
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/coach/programs/${id}/duplicate`);
      toast.success(`Duplicated as "${data.name}". Assign to another athlete or team.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to duplicate");
    }
  };

  const startNew = () =>
    setEditing({
      name: "",
      description: "",
      weeks: 4,
      sessions_per_week: 3,
      program_type: "microcycle",
      days: [newDay(1, 1)],
      tags: [],
    });

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="coach-programs-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tiny !text-yellow-400 mb-2">// COACH MODE · MANUAL BUILDER</div>
          <h1 className="font-heading text-4xl sm:text-5xl">PROGRAM BUILDER</h1>
          <p className="text-text-secondary text-sm mt-2 max-w-2xl">
            Design multi-week programs block-by-block: weeks, days, exercises, sets/reps/rest, notes. Save as templates and assign to any client or team.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setImportOpen(true)}
            className="border border-white/20 hover:!border-yellow-400 hover:!text-yellow-400 text-white/85 px-4 py-3 text-xs label-tiny tracking-widest flex items-center gap-2 transition-colors"
            data-testid="import-share-code-button"
          >
            <DownloadSimple size={13} weight="bold" /> IMPORT CODE
          </button>
          <button
            onClick={() => setSeedOpen(true)}
            className="border border-white/20 hover:!border-yellow-400 hover:!text-yellow-400 text-white/85 px-4 py-3 text-xs label-tiny tracking-widest flex items-center gap-2 transition-colors"
            data-testid="seed-template-button"
          >
            <Stack size={13} weight="bold" /> SEED TEMPLATE
          </button>
          <button
            onClick={startNew}
            className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors px-5 py-3 text-xs label-tiny tracking-widest flex items-center gap-2"
            data-testid="new-program-button"
          >
            <Plus size={14} weight="bold" /> NEW PROGRAM
          </button>
        </div>
      </div>

      {programs.length === 0 ? (
        <div className="tactical-card p-12 text-center" data-testid="programs-empty">
          <ListChecks size={40} weight="duotone" className="text-brand-primary mx-auto mb-4" />
          <h3 className="font-heading text-xl mb-2">NO PROGRAMS YET</h3>
          <p className="text-sm text-text-secondary">Build a template once, assign to any client.</p>
        </div>
      ) : (
        <>
          <div
            className="flex flex-wrap items-center gap-2 mb-4"
            data-testid="programs-filter-bar"
            role="tablist"
            aria-label="Filter programs by source"
          >
            {[
              { id: "all", label: "ALL" },
              { id: "manual", label: "MANUAL" },
              { id: "ai", label: "AI" },
              { id: "imported", label: "SHARED" },
            ].map((f) => {
              const active = filterMode === f.id;
              const count = filterCounts[f.id];
              return (
                <button
                  key={f.id}
                  onClick={() => setFilterMode(f.id)}
                  role="tab"
                  aria-selected={active}
                  data-testid={`filter-${f.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs label-tiny tracking-widest border transition-colors ${active
                    ? "!bg-yellow-400 text-slate-900 !border-yellow-400 font-bold"
                    : "border-white/20 text-white/80 hover:!border-yellow-400 hover:!text-yellow-400"}`}
                >
                  {f.label}
                  <span className={`text-[10px] ${active ? "text-slate-800" : "text-white/50"}`}>{count}</span>
                </button>
              );
            })}
          </div>
          {/* Search + sort row — pulled out from filter tabs so both compact
              chips and search stay independently scannable when the library grows. */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="programs-search-sort">
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={13} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, description, or tag..."
                className="w-full bg-surface-elevated border border-white/15 pl-8 pr-8 py-2 text-sm text-slate-800 placeholder:text-slate-400"
                data-testid="programs-search-input"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:!text-brand-heat"
                  data-testid="programs-search-clear"
                  aria-label="Clear search"
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="bg-surface-elevated border border-white/15 px-3 py-2 text-xs label-tiny tracking-widest text-slate-800 focus:outline-none focus:border-brand-accent"
              data-testid="programs-sort-select"
              aria-label="Sort programs"
            >
              <option value="recent">SORT: RECENT</option>
              <option value="assigned">SORT: ASSIGNED COUNT</option>
              <option value="az">SORT: A → Z</option>
            </select>
          </div>
          {filteredPrograms.length === 0 ? (
            <div className="tactical-card p-8 text-center" data-testid="programs-filter-empty">
              {search
                ? <p className="text-sm text-white/70">No programs match &ldquo;<span className="!text-yellow-400">{search}</span>&rdquo;.</p>
                : <p className="text-sm text-white/70">No <span className="!text-yellow-400">{filterMode}</span> programs yet.</p>
              }
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="programs-list">
              {filteredPrograms.map((p) => (
            <div key={p.program_id} className="tactical-card p-5" data-testid={`program-${p.program_id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-heading text-lg">{p.name}</h3>
                <div className="flex flex-wrap gap-1 justify-end shrink-0">
                  {isAiProgram(p) && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] label-tiny tracking-wider border !border-yellow-400/60 !text-yellow-400 whitespace-nowrap"
                      data-testid={`ai-chip-${p.program_id}`}
                      title="Generated by AI Program Builder"
                    >
                      <Sparkle size={9} weight="fill" /> AI
                    </span>
                  )}
                  {p.imported_from && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] label-tiny tracking-wider border !border-yellow-400/60 !text-yellow-400 whitespace-nowrap"
                      data-testid={`imported-chip-${p.program_id}`}
                      title={`Shared by ${p.imported_from.author_name}`}
                    >
                      <DownloadSimple size={9} weight="bold" /> SHARED
                    </span>
                  )}
                </div>
              </div>
              {p.description && <p className="text-xs text-text-secondary mb-3 line-clamp-2">{p.description}</p>}
              {p.imported_from && (
                <p className="text-[11px] text-text-secondary mb-3 italic" data-testid={`imported-from-${p.program_id}`}>
                  from <span className="!text-yellow-400">{p.imported_from.author_name}</span>
                </p>
              )}
              <div className="flex flex-wrap gap-3 label-tiny mb-4">
                <span className="!text-yellow-400">{p.weeks}W</span>
                <span>{p.days?.length || 0} SESSIONS</span>
                {p.sessions_per_week && <span className="text-text-secondary">{p.sessions_per_week}/WK</span>}
                <span className="text-text-secondary">· {p.assignment_count || 0} ASSIGNED</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setAssigning(p)}
                  className="flex-1 min-w-[140px] !bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors py-2 text-xs label-tiny tracking-widest"
                  data-testid={`assign-${p.program_id}`}
                >
                  ASSIGN
                </button>
                <button
                  onClick={() => setEditing(p)}
                  className="border border-white/10 hover:!border-yellow-400 px-3 transition-colors"
                  data-testid={`edit-${p.program_id}`}
                  aria-label="Edit program"
                >
                  <PencilSimple size={13} />
                </button>
                <button
                  onClick={() => duplicate(p.program_id)}
                  className="border border-white/10 hover:!border-yellow-400 px-3 transition-colors"
                  data-testid={`duplicate-${p.program_id}`}
                  aria-label="Duplicate program"
                  title="Duplicate — clone this plan so you can reassign to another athlete or team"
                >
                  <Copy size={13} />
                </button>
                <button
                  onClick={() => setCopyingProgram(p)}
                  className="border border-white/10 hover:!border-yellow-400 px-3 transition-colors"
                  data-testid={`copy-content-${p.program_id}`}
                  aria-label="Copy weeks / sessions / exercises to another program"
                  title="Copy sessions, weeks, or blocks into another program"
                >
                  <ArrowRight size={13} />
                </button>
                <button
                  onClick={() => setSharingProgram(p)}
                  className="border border-white/10 hover:!border-yellow-400 hover:!text-yellow-400 px-3 transition-colors"
                  data-testid={`share-${p.program_id}`}
                  aria-label="Share program with a code"
                  title="Generate a share code to send this program to another coach"
                >
                  <Share size={13} />
                </button>
                <button
                  onClick={() => remove(p.program_id)}
                  className="border border-white/10 hover:border-red-400 hover:text-red-400 px-3 transition-colors text-text-secondary"
                  data-testid={`delete-${p.program_id}`}
                  aria-label="Delete program"
                >
                  <Trash size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
          )}
        </>
      )}

      {editing && (
        <ProgramEditor
          program={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {assigning && (
        <AssignDialog program={assigning} clients={clients} teams={teams} onClose={() => setAssigning(null)} />
      )}
      {copyingProgram && (
        <CopyProgramDialog
          program={copyingProgram}
          allPrograms={programs.filter((p) => p.program_id !== copyingProgram.program_id)}
          onClose={() => setCopyingProgram(null)}
          onCopied={() => { setCopyingProgram(null); load(); }}
        />
      )}
      {sharingProgram && (
        <ShareProgramDialog
          program={sharingProgram}
          onClose={() => setSharingProgram(null)}
        />
      )}
      {importOpen && (
        <ImportProgramDialog
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); load(); }}
        />
      )}
      {seedOpen && (
        <SeedTemplateDialog
          onClose={() => setSeedOpen(false)}
          onSeeded={() => { setSeedOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function SeedTemplateDialog({ onClose, onSeeded }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seedingId, setSeedingId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [goalFilter, setGoalFilter] = useState("all");

  useEffect(() => {
    api.get("/coach/program-templates")
      .then(({ data }) => setTemplates(data || []))
      .catch(() => toast.error("Could not load templates"))
      .finally(() => setLoading(false));
  }, []);

  // Derive unique goal chips from the loaded templates so the filter row
  // grows naturally when new templates ship with new goals.
  const goals = useMemo(() => {
    const seen = new Set();
    const out = [];
    templates.forEach((t) => {
      const g = (t.goal || "").trim();
      if (g && !seen.has(g)) { seen.add(g); out.push(g); }
    });
    return out;
  }, [templates]);

  const filtered = useMemo(() => (
    goalFilter === "all" ? templates : templates.filter((t) => (t.goal || "") === goalFilter)
  ), [templates, goalFilter]);

  const seed = async (t) => {
    setSeedingId(t.template_id);
    try {
      const { data } = await api.post(`/coach/program-templates/${t.template_id}/copy`);
      toast.success(`"${data.name}" seeded into your library`);
      onSeeded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally {
      setSeedingId(null);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/20 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="seed-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">SEED A STARTER TEMPLATE</DialogTitle>
          <DialogDescription className="text-xs text-white/70">
            Drop a proven program straight into your library — edit freely from there. Great for onboarding new clients in one click.
          </DialogDescription>
        </DialogHeader>
        {!loading && goals.length > 1 && (
          <div className="flex gap-1.5 flex-wrap py-2" data-testid="template-goal-filters">
            <GoalChip active={goalFilter === "all"} count={templates.length} onClick={() => setGoalFilter("all")} testid="goal-chip-all">
              ALL
            </GoalChip>
            {goals.map((g) => (
              <GoalChip
                key={g}
                active={goalFilter === g}
                count={templates.filter((t) => (t.goal || "") === g).length}
                onClick={() => setGoalFilter(g)}
                testid={`goal-chip-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {g.toUpperCase()}
              </GoalChip>
            ))}
          </div>
        )}
        {loading ? (
          <div className="py-6 text-sm text-white/60">Loading templates…</div>
        ) : filtered.length === 0 ? (
          <div className="py-6 text-sm text-text-secondary text-center">No templates for this goal yet — check back soon.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map((t) => (
              <div key={t.template_id} className="tactical-card p-4" data-testid={`template-card-${t.template_id}`}>
                <div className="label-tiny text-brand-heat mb-1">// {t.goal?.toUpperCase() || "PROGRAM"}</div>
                <h3 className="font-heading text-lg mb-2">{t.name}</h3>
                <p className="text-xs text-text-secondary mb-3 line-clamp-3">{t.description}</p>
                <div className="flex items-center gap-3 label-tiny text-[10px] text-text-secondary mb-4">
                  <span className="tabular-nums text-brand-accent">{t.weeks}W</span>
                  <span>·</span>
                  <span className="tabular-nums">{t.days_per_week}/wk</span>
                  <span>·</span>
                  <span className="tabular-nums">{t.day_count} sessions</span>
                  <span>·</span>
                  <span className="tabular-nums">{t.exercise_count} exercises</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPreviewId(t.template_id)}
                    className="flex-1 border border-white/25 hover:!border-brand-accent hover:!text-brand-accent text-white/85 px-4 py-2 text-xs label-tiny tracking-wider transition"
                    data-testid={`template-preview-${t.template_id}`}
                  >
                    PREVIEW
                  </button>
                  <button
                    onClick={() => seed(t)}
                    disabled={!!seedingId}
                    className="flex-1 bg-brand-accent text-surface-base hover:bg-brand-accent-hover px-4 py-2 text-xs label-tiny tracking-wider disabled:opacity-50 transition"
                    data-testid={`template-seed-${t.template_id}`}
                  >
                    {seedingId === t.template_id ? "SEEDING…" : "SEED TO LIBRARY"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      {previewId && (
        <TemplatePreviewDialog
          templateId={previewId}
          onClose={() => setPreviewId(null)}
          onSeed={() => {
            const t = templates.find((x) => x.template_id === previewId);
            if (t) { setPreviewId(null); seed(t); }
          }}
          seeding={seedingId === previewId}
        />
      )}
    </Dialog>
  );
}

function GoalChip({ active, count, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] label-tiny tracking-widest border transition-colors ${
        active
          ? "bg-brand-accent text-surface-base border-brand-accent"
          : "border-white/20 text-white/80 hover:!border-brand-accent hover:!text-brand-accent"
      }`}
    >
      <span>{children}</span>
      <span className={`tabular-nums text-[9px] ${active ? "opacity-80" : "text-text-secondary"}`}>{count}</span>
    </button>
  );
}

function TemplatePreviewDialog({ templateId, onClose, onSeed, seeding }) {
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openWeek, setOpenWeek] = useState(1);

  useEffect(() => {
    setLoading(true);
    api.get(`/coach/program-templates/${templateId}`)
      .then(({ data }) => setTpl(data))
      .catch(() => toast.error("Could not load preview"))
      .finally(() => setLoading(false));
  }, [templateId]);

  // Group days by week
  const weeks = useMemo(() => {
    const map = new Map();
    (tpl?.days || []).forEach((d) => {
      const w = d.week || 1;
      if (!map.has(w)) map.set(w, []);
      map.get(w).push(d);
    });
    return [...map.entries()].map(([week, days]) => ({ week, days })).sort((a, b) => a.week - b.week);
  }, [tpl]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/20 text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-preview-dialog">
        {loading || !tpl ? (
          <div className="py-8 text-center text-sm text-white/60">Loading week-by-week structure…</div>
        ) : (
          <>
            <DialogHeader>
              <div className="label-tiny text-brand-heat mb-1">// {tpl.goal?.toUpperCase()}</div>
              <DialogTitle className="font-heading text-2xl">{tpl.name}</DialogTitle>
              <DialogDescription className="text-xs text-white/70 whitespace-pre-wrap">
                {tpl.description}
              </DialogDescription>
            </DialogHeader>

            {/* Week tabs */}
            <div className="flex gap-1 flex-wrap mt-3 mb-4 border-b border-white/10 pb-2">
              {weeks.map((w) => (
                <button
                  key={w.week}
                  onClick={() => setOpenWeek(w.week)}
                  className={`px-3 py-1.5 text-xs label-tiny tracking-widest border transition-colors ${
                    openWeek === w.week
                      ? "border-brand-accent bg-brand-accent text-surface-base"
                      : "border-white/20 text-white/80 hover:!border-brand-accent hover:!text-brand-accent"
                  }`}
                  data-testid={`preview-week-${w.week}`}
                >
                  WEEK {w.week}
                </button>
              ))}
            </div>

            {/* Sessions in the active week */}
            <div className="space-y-3">
              {(weeks.find((w) => w.week === openWeek)?.days || []).map((d, i) => (
                <div key={i} className="tactical-card p-4" data-testid={`preview-day-${openWeek}-${d.day || i + 1}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3">
                      <span className="label-tiny text-brand-accent text-[10px] tabular-nums">D{d.day || i + 1}</span>
                      <span className="font-heading text-base">{(d.name || `Session ${i + 1}`).toUpperCase()}</span>
                      {d.session_type && (
                        <span className="label-tiny text-[10px] text-text-secondary">· {d.session_type}</span>
                      )}
                    </div>
                    <span className="label-tiny text-[10px] text-text-secondary tabular-nums">
                      {(d.exercises || []).length} exercises
                    </span>
                  </div>
                  {(d.exercises || []).length > 0 && (
                    <div className="border-t border-white/5 pt-2 space-y-1.5">
                      {d.exercises.map((ex, ei) => (
                        <div key={ei} className="grid grid-cols-12 items-center gap-2 text-xs" data-testid={`preview-ex-${openWeek}-${d.day || i+1}-${ei}`}>
                          <span className="col-span-6 truncate">{ex.name}</span>
                          <span className="col-span-1 label-tiny tabular-nums text-text-secondary">{ex.sets || "—"}×</span>
                          <span className="col-span-2 tabular-nums text-text-secondary truncate" title={ex.reps}>{ex.reps || "—"}</span>
                          <span className="col-span-1 label-tiny tabular-nums text-brand-accent">
                            {ex.percentage_1rm ? `${ex.percentage_1rm}%` : ex.rpe ? `RPE${ex.rpe}` : ""}
                          </span>
                          <span className="col-span-2 label-tiny tabular-nums text-text-secondary text-right">
                            {ex.rest_sec ? `${ex.rest_sec}s rest` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Sticky CTA */}
            <div className="sticky bottom-0 -mx-6 px-6 pt-4 mt-4 border-t border-white/10 bg-surface-DEFAULT flex justify-end gap-2">
              <button
                onClick={onClose}
                className="border border-white/20 hover:!border-white/50 px-4 py-2 text-xs label-tiny tracking-wider"
                data-testid="preview-close"
              >
                CLOSE
              </button>
              <button
                onClick={onSeed}
                disabled={seeding}
                className="bg-brand-accent text-surface-base hover:bg-brand-accent-hover px-5 py-2 text-xs label-tiny tracking-wider disabled:opacity-50 transition"
                data-testid="preview-seed"
              >
                {seeding ? "SEEDING…" : "SEED TO LIBRARY"}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProgramEditor({ program, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: program.name || "",
    description: program.description || "",
    weeks: program.weeks || 4,
    sessions_per_week: program.sessions_per_week || 3,
    program_type: program.program_type || "microcycle",
    days: program.days?.length > 0 ? program.days : [newDay(1, 1)],
    tags: program.tags || [],
  });
  const [activeWeek, setActiveWeek] = useState(1);
  const [saving, setSaving] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [favoritesTick, setFavoritesTick] = useState(0);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [finisherOpen, setFinisherOpen] = useState(false);
  // Track the current server-side program to expose last_scheme / last_finisher meta
  // in the header CTA (so the Finish Mesocycle button stays visible across dialog opens).
  const [serverMeta, setServerMeta] = useState({
    last_scheme: program.last_scheme || null,
    last_finisher: program.last_finisher || null,
  });

  const addExerciseToDay = (week, day, exercise) => {
    setForm((f) => ({
      ...f,
      days: f.days.map((d) => (d.week === week && d.day === day
        ? { ...d, exercises: [...(d.exercises || []), { ...exercise }] }
        : d)),
    }));
  };

  // Re-fetch the program from server (used after apply-scheme, which mutates server-side).
  const reloadProgram = async () => {
    try {
      const { data } = await api.get(`/coach/programs/${program.program_id}`);
      setForm((f) => ({
        ...f,
        name: data.name || f.name,
        weeks: data.weeks || f.weeks,
        days: data.days?.length ? data.days : f.days,
      }));
      setServerMeta({
        last_scheme: data.last_scheme || null,
        last_finisher: data.last_finisher || null,
      });
    } catch (e) {
      toast.error("Reload failed");
    }
  };

  // Group days by week for tab rendering
  const daysByWeek = useMemo(() => {
    const g = {};
    for (const d of form.days) {
      const w = Number(d.week) || 1;
      g[w] = g[w] || [];
      g[w].push(d);
    }
    Object.keys(g).forEach((k) => g[k].sort((a, b) => (a.day || 0) - (b.day || 0)));
    return g;
  }, [form.days]);

  const weeksList = useMemo(() => {
    const w = Object.keys(daysByWeek).map(Number).sort((a, b) => a - b);
    return w.length ? w : [1];
  }, [daysByWeek]);

  const currentDays = daysByWeek[activeWeek] || [];

  // Helper: replace one day (identified by (week,day)) with an updated one
  const replaceDay = (targetWeek, targetDay, updater) => {
    setForm((f) => ({
      ...f,
      days: f.days.map((d) =>
        d.week === targetWeek && d.day === targetDay ? updater(d) : d
      ),
    }));
  };

  const addDayToWeek = () => {
    const existing = daysByWeek[activeWeek] || [];
    const nextDayNum = (existing.reduce((mx, d) => Math.max(mx, d.day || 0), 0) || 0) + 1;
    setForm((f) => ({ ...f, days: [...f.days, newDay(activeWeek, nextDayNum)] }));
  };

  const removeDay = (targetWeek, targetDay) => {
    setForm((f) => ({
      ...f,
      days: f.days.filter((d) => !(d.week === targetWeek && d.day === targetDay)),
    }));
  };

  const duplicateDay = (targetWeek, targetDay) => {
    const src = form.days.find((d) => d.week === targetWeek && d.day === targetDay);
    if (!src) return;
    const existing = daysByWeek[activeWeek] || [];
    const nextDayNum = existing.reduce((mx, d) => Math.max(mx, d.day || 0), 0) + 1;
    const clone = {
      ...src,
      day: nextDayNum,
      exercises: (src.exercises || []).map((e) => ({ ...e })),
    };
    setForm((f) => ({ ...f, days: [...f.days, clone] }));
  };

  const moveDay = (targetWeek, targetDay, dir) => {
    const list = [...(daysByWeek[targetWeek] || [])];
    const idx = list.findIndex((d) => d.day === targetDay);
    if (idx < 0) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const a = list[idx];
    const b = list[swapIdx];
    // Swap day numbers
    setForm((f) => ({
      ...f,
      days: f.days.map((d) => {
        if (d.week !== targetWeek) return d;
        if (d.day === a.day) return { ...d, day: b.day };
        if (d.day === b.day) return { ...d, day: a.day };
        return d;
      }),
    }));
  };

  const addWeek = () => {
    const nextWeek = Math.max(...weeksList) + 1;
    setForm((f) => ({
      ...f,
      weeks: Math.max(f.weeks, nextWeek),
      days: [...f.days, newDay(nextWeek, 1)],
    }));
    setActiveWeek(nextWeek);
  };

  const duplicateWeek = (sourceWeek) => {
    const src = daysByWeek[sourceWeek] || [];
    if (src.length === 0) return;
    const nextWeek = Math.max(...weeksList) + 1;
    const cloned = src.map((d) => ({
      ...d,
      week: nextWeek,
      exercises: (d.exercises || []).map((e) => ({ ...e })),
    }));
    setForm((f) => ({
      ...f,
      weeks: Math.max(f.weeks, nextWeek),
      days: [...f.days, ...cloned],
    }));
    setActiveWeek(nextWeek);
    toast.success(`Week ${sourceWeek} duplicated as Week ${nextWeek}`);
  };

  const removeWeek = (targetWeek) => {
    if (weeksList.length <= 1) {
      toast.error("Program needs at least one week");
      return;
    }
    if (!window.confirm(`Remove all sessions in Week ${targetWeek}?`)) return;
    setForm((f) => ({ ...f, days: f.days.filter((d) => d.week !== targetWeek) }));
    // Move to nearest remaining week
    const remaining = weeksList.filter((w) => w !== targetWeek);
    setActiveWeek(remaining[0] || 1);
  };

  const updateExercise = (day, exIdx, key, val) => {
    replaceDay(day.week, day.day, (d) => ({
      ...d,
      exercises: d.exercises.map((e, i) => (i === exIdx ? { ...e, [key]: val } : e)),
    }));
  };

  const addExercise = (day) =>
    replaceDay(day.week, day.day, (d) => ({
      ...d,
      exercises: [...(d.exercises || []), { ...EMPTY_EX }],
    }));

  const removeExercise = (day, exIdx) =>
    replaceDay(day.week, day.day, (d) => ({
      ...d,
      exercises: d.exercises.filter((_, i) => i !== exIdx),
    }));

  const replaceExercises = (day, newExercises) =>
    replaceDay(day.week, day.day, (d) => ({ ...d, exercises: newExercises }));

  const copyExerciseToDay = ({ sourceDayId, targetDay, exercise }) => {
    const [tw, td] = targetDay.split("-").map(Number);
    setForm((f) => ({
      ...f,
      days: f.days.map((d) =>
        d.week === tw && d.day === td
          ? { ...d, exercises: [...(d.exercises || []), { ...exercise }] }
          : d
      ),
    }));
    toast.success(`Copied to Week ${tw} · Day ${td}`);
  };

  // Per-day quick copy: append the whole day's exercises into a target day (any week).
  // Rest days are skipped as targets. Existing exercises on the target are preserved (append).
  const copyDayToDay = ({ sourceWeek, sourceDay, targetKey }) => {
    const [tw, td] = String(targetKey).split("-").map(Number);
    if (tw === sourceWeek && td === sourceDay) return;
    const src = form.days.find((d) => d.week === sourceWeek && d.day === sourceDay);
    if (!src) return;
    const target = form.days.find((d) => d.week === tw && d.day === td);
    if (target?.is_rest) {
      toast.error(`Week ${tw} · Day ${td} is a rest day — skipped`);
      return;
    }
    const cloned = (src.exercises || []).map((e) => ({ ...e }));
    if (target) {
      setForm((f) => ({
        ...f,
        days: f.days.map((d) => (d.week === tw && d.day === td
          ? { ...d, exercises: [...(d.exercises || []), ...cloned] }
          : d)),
      }));
    } else {
      setForm((f) => ({
        ...f,
        days: [...f.days, {
          week: tw, day: td,
          name: src.name || "",
          session_type: src.session_type || "strength",
          is_rest: false,
          exercises: cloned,
        }],
      }));
    }
    toast.success(`Day copied to Week ${tw} · Day ${td}`);
  };

  // Mirror this day across every other week (1..form.weeks) at the same day slot.
  // - Existing non-rest target with content → append cloned exercises.
  // - Existing rest day → skipped with counter.
  // - Missing slot → create a new day cloned from source.
  const mirrorDayAcrossWeeks = ({ sourceWeek, sourceDay }) => {
    const src = form.days.find((d) => d.week === sourceWeek && d.day === sourceDay);
    if (!src) return;
    const totalWeeks = Math.max(Number(form.weeks) || weeksList.length || 1, ...weeksList);
    let appended = 0, created = 0, skippedRest = 0;
    setForm((f) => {
      const next = [...f.days];
      for (let w = 1; w <= totalWeeks; w++) {
        if (w === sourceWeek) continue;
        const idx = next.findIndex((d) => d.week === w && d.day === sourceDay);
        const cloned = (src.exercises || []).map((e) => ({ ...e }));
        if (idx === -1) {
          next.push({
            week: w, day: sourceDay,
            name: src.name || "",
            session_type: src.session_type || "strength",
            is_rest: false,
            exercises: cloned,
          });
          created += 1;
        } else if (next[idx].is_rest) {
          skippedRest += 1;
        } else {
          next[idx] = { ...next[idx], exercises: [...(next[idx].exercises || []), ...cloned] };
          appended += 1;
        }
      }
      return { ...f, days: next };
    });
    const parts = [];
    if (created) parts.push(`${created} created`);
    if (appended) parts.push(`${appended} appended`);
    if (skippedRest) parts.push(`${skippedRest} rest skipped`);
    toast.success(`Mirrored across ${totalWeeks - 1} week${totalWeeks - 1 === 1 ? "" : "s"}${parts.length ? ` — ${parts.join(" · ")}` : ""}`);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Program name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        weeks: Number(form.weeks) || weeksList.length,
        sessions_per_week: Number(form.sessions_per_week) || null,
        days: form.days.map((d) => ({
          ...d,
          week: Number(d.week) || 1,
          day: Number(d.day) || 1,
          exercises: (d.exercises || [])
            .filter((e) => (e.name || "").trim())
            .map((e) => ({
              ...e,
              sets: Number(e.sets) || 0,
              rest_sec: Number(e.rest_sec) || 0,
            })),
        })),
      };
      if (program.program_id) {
        await api.patch(`/coach/programs/${program.program_id}`, payload);
        toast.success("Program updated");
      } else {
        await api.post("/coach/programs", payload);
        toast.success("Program created");
      }
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="bg-surface-DEFAULT border-white/10 text-white max-w-5xl max-h-[92vh] overflow-y-auto"
        data-testid="program-editor-dialog"
        onPointerDownOutside={(e) => {
          if (e.target?.closest?.('[data-testid="favorites-palette"]')) e.preventDefault();
          if (e.target?.closest?.('[data-testid="library-palette"]')) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (e.target?.closest?.('[data-testid="favorites-palette"]')) e.preventDefault();
          if (e.target?.closest?.('[data-testid="library-palette"]')) e.preventDefault();
        }}
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <DialogTitle className="font-heading text-2xl">
              {program.program_id ? "EDIT PROGRAM" : "NEW PROGRAM"}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {program.program_id && (
                <button
                  type="button"
                  onClick={() => setFinisherOpen(true)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs label-tiny tracking-wider border transition ${serverMeta.last_scheme ? "!border-yellow-400 !text-yellow-400 hover:!bg-yellow-400 hover:!text-slate-900" : "border-white/20 text-white/85 hover:!border-yellow-400 hover:!text-yellow-400"}`}
                  data-testid="finish-mesocycle-btn"
                  title="Append a Deload or Peak week"
                >
                  <ArrowRight size={12} />
                  {serverMeta.last_finisher ? "MANAGE FINISHER" : "FINISH MESOCYCLE"}
                </button>
              )}
              <button
                type="button"
                onClick={() => { setLibraryOpen((v) => !v); if (!libraryOpen) setFavoritesOpen(false); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs label-tiny tracking-wider border transition ${libraryOpen ? "!bg-brand-accent text-slate-900 !border-brand-accent" : "border-white/20 text-white/85 hover:!border-brand-accent hover:!text-brand-accent"}`}
                data-testid="toggle-library-btn"
                title="Toggle Exercise Library palette — drag rows onto sessions"
              >
                <Books size={12} weight={libraryOpen ? "fill" : "regular"} /> {libraryOpen ? "HIDE" : "LIBRARY"}
              </button>
              <button
                type="button"
                onClick={() => { setFavoritesOpen((v) => !v); if (!favoritesOpen) setLibraryOpen(false); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs label-tiny tracking-wider border transition ${favoritesOpen ? "!bg-yellow-400 text-slate-900 !border-yellow-400" : "border-white/20 text-white/85 hover:!border-yellow-400 hover:!text-yellow-400"}`}
                data-testid="toggle-favorites-btn"
                title="Toggle Favorites palette"
              >
                <Star size={12} weight={favoritesOpen ? "fill" : "regular"} /> {favoritesOpen ? "HIDE" : "FAVORITES"}
              </button>
            </div>
          </div>
          {serverMeta.last_scheme && !serverMeta.last_finisher && (
            <div className="mt-2 text-[11px] text-white/70" data-testid="finisher-hint-line">
              Last scheme: <span className="!text-yellow-400 font-bold">{(serverMeta.last_scheme.scheme || "").toUpperCase()}</span> on <span className="!text-yellow-400">{serverMeta.last_scheme.exercise_name}</span> — ready to cap with Deload / Peak.
            </div>
          )}
          {serverMeta.last_finisher && (
            <div className="mt-2 text-[11px] text-white/70" data-testid="finisher-active-line">
              Finisher active: <span className="!text-yellow-400 font-bold uppercase">{serverMeta.last_finisher.kind}</span> on week {serverMeta.last_finisher.week} · {serverMeta.last_finisher.exercise_name}
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Meta */}
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-3">
              <label className="label-tiny block mb-2">NAME</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="8-Week Hypertrophy"
                className="bg-surface-elevated border-white/10"
                data-testid="program-name-input"
              />
            </div>
            <div>
              <label className="label-tiny block mb-2">WEEKS</label>
              <Input
                type="number"
                min={1}
                value={form.weeks}
                onChange={(e) => setForm({ ...form, weeks: e.target.value })}
                className="bg-surface-elevated border-white/10"
                data-testid="program-weeks-input"
              />
            </div>
            <div>
              <label className="label-tiny block mb-2">SESSIONS/WK</label>
              <Input
                type="number"
                min={1}
                max={7}
                value={form.sessions_per_week}
                onChange={(e) => setForm({ ...form, sessions_per_week: e.target.value })}
                className="bg-surface-elevated border-white/10"
                data-testid="program-sessions-input"
              />
            </div>
            <div>
              <label className="label-tiny block mb-2">TOTAL DAYS</label>
              <div className="h-10 flex items-center px-3 border border-white/10 bg-surface-elevated !text-yellow-400 tabular-nums text-sm" data-testid="program-total-days">
                {form.days.length}
              </div>
            </div>
          </div>
          <div>
            <label className="label-tiny block mb-2">PROGRAM TYPE</label>
            <div className="flex flex-wrap gap-2" data-testid="program-type-selector">
              {PROGRAM_TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setForm({ ...form, program_type: t.id })}
                  data-testid={`program-type-${t.id}`}
                  className={`px-3 py-2 text-xs label-tiny tracking-wider border transition ${
                    (form.program_type || "microcycle") === t.id
                      ? "!border-yellow-400 !bg-yellow-400/10 !text-yellow-400"
                      : "border-white/10 text-text-secondary hover:border-white/40 hover:text-white"
                  }`}
                >
                  {t.label.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-tiny block mb-2">DESCRIPTION</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What is this program? Target audience? Key stimulus?"
              className="bg-surface-elevated border-white/10"
              data-testid="program-desc-input"
            />
          </div>

          {/* Week tabs */}
          <div className="pt-2 border-t border-white/10">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <label className="label-tiny">WEEKS</label>
              <div className="flex gap-2">
                <button
                  onClick={() => duplicateWeek(activeWeek)}
                  className="text-xs !text-yellow-400 hover:underline flex items-center gap-1"
                  data-testid="duplicate-week-button"
                >
                  <Copy size={12} /> DUPLICATE WEEK {activeWeek}
                </button>
                <button
                  onClick={addWeek}
                  className="text-xs !text-yellow-400 hover:underline flex items-center gap-1"
                  data-testid="add-week-button"
                >
                  <Plus size={12} /> ADD WEEK
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mb-4 items-center" data-testid="week-tabs">
              {weeksList.map((w) => (
                <div key={w} className="flex items-center">
                  <button
                    onClick={() => setActiveWeek(w)}
                    className={`px-3 py-1.5 label-tiny border transition-colors ${
                      activeWeek === w
                        ? "!border-yellow-400 !text-yellow-400 !bg-yellow-400/5"
                        : "border-white/10 text-text-secondary hover:text-white hover:border-white/40"
                    }`}
                    data-testid={`week-tab-${w}`}
                  >
                    WEEK {w}
                    <span className="ml-1 text-text-secondary/60">({daysByWeek[w]?.length || 0})</span>
                  </button>
                  {weeksList.length > 1 && (
                    <button
                      onClick={() => removeWeek(w)}
                      className="ml-0.5 px-1.5 py-1.5 border border-l-0 border-white/10 text-text-secondary hover:text-red-400 hover:border-red-400/40 transition-colors"
                      data-testid={`remove-week-${w}`}
                      aria-label={`Remove week ${w}`}
                    >
                      <X size={10} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Days in active week */}
            <div className="flex items-center justify-between mb-2">
              <label className="label-tiny">WEEK {activeWeek} · {currentDays.length} SESSIONS</label>
              <button
                onClick={addDayToWeek}
                className="text-xs !text-yellow-400 hover:underline flex items-center gap-1"
                data-testid="add-day-button"
              >
                <Plus size={12} /> ADD SESSION
              </button>
            </div>

            <div className="space-y-3">
              {currentDays.length > 0 && <WeeklyLoadBar days={currentDays} />}
              {currentDays.length === 0 ? (
                <div className="border border-dashed border-white/10 p-8 text-center text-sm text-text-secondary">
                  No sessions in week {activeWeek}. Click ADD SESSION to start.
                </div>
              ) : (
                currentDays.map((d, di) => (
                  <SessionBuilder
                    key={`${d.week}-${d.day}`}
                    day={d}
                    dayIndex={di}
                    totalDays={currentDays.length}
                    weekDays={currentDays}
                    allDays={form.days}
                    programId={program.program_id}
                    totalWeeks={Number(form.weeks) || 4}
                    sessionTypes={SESSION_TYPES}
                    onUpdateName={(name) => replaceDay(d.week, d.day, (x) => ({ ...x, name }))}
                    onUpdateSessionType={(t) => replaceDay(d.week, d.day, (x) => ({ ...x, session_type: t }))}
                    onToggleRest={() => replaceDay(d.week, d.day, (x) => ({ ...x, is_rest: !x.is_rest }))}
                    onRemove={() => removeDay(d.week, d.day)}
                    onDuplicate={() => duplicateDay(d.week, d.day)}
                    onMoveUp={() => moveDay(d.week, d.day, "up")}
                    onMoveDown={() => moveDay(d.week, d.day, "down")}
                    onReplaceExercises={(next) => replaceExercises(d, next)}
                    onAddEx={() => addExercise(d)}
                    onCopyExercise={copyExerciseToDay}
                    onCopyDayToDay={(target) => copyDayToDay({ sourceWeek: d.week, sourceDay: d.day, targetKey: target })}
                    onMirrorDayAcrossWeeks={() => mirrorDayAcrossWeeks({ sourceWeek: d.week, sourceDay: d.day })}
                    onSchemeApplied={reloadProgram}
                    onFavoritesChanged={() => setFavoritesTick((n) => n + 1)}
                  />
                ))
              )}
            </div>
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50"
            data-testid="save-program-button"
          >
            {saving ? "SAVING..." : (program.program_id ? "UPDATE PROGRAM" : "CREATE PROGRAM")}
          </button>
        </div>
      </DialogContent>
      <FavoritesPalette
        open={favoritesOpen}
        onClose={() => setFavoritesOpen(false)}
        onAddToDay={addExerciseToDay}
        days={form.days}
        currentWeek={activeWeek}
        refreshKey={favoritesTick}
      />
      <LibraryPalette
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onAddToDay={addExerciseToDay}
        days={form.days}
        currentWeek={activeWeek}
      />
      {finisherOpen && program.program_id && (
        <MesocycleFinisherDialog
          programId={program.program_id}
          defaultSourceDay={serverMeta.last_scheme?.source_day || 1}
          defaultSourceSection={serverMeta.last_scheme?.source_section || "strength"}
          defaultExerciseName={serverMeta.last_scheme?.exercise_name || ""}
          defaultSessionType={serverMeta.last_scheme?.session_type || "strength"}
          defaultBase1rm={serverMeta.last_scheme?.base_1rm || 100}
          onClose={() => setFinisherOpen(false)}
          onApplied={reloadProgram}
        />
      )}
    </Dialog>
  );
}


function AssignDialog({ program, clients, teams, onClose }) {
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [assigning, setAssigning] = useState(false);

  const toggle = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const assign = async () => {
    if (selectedClients.length === 0 && selectedTeams.length === 0) {
      return toast.error("Pick at least one client or team");
    }
    setAssigning(true);
    try {
      const { data } = await api.post(`/coach/programs/${program.program_id}/assign-bulk`, {
        client_ids: selectedClients,
        team_ids: selectedTeams,
        start_date: startDate,
      });
      const parts = [];
      if (data.assigned_count) parts.push(`${data.assigned_count} assigned`);
      if (data.skipped_count) parts.push(`${data.skipped_count} skipped`);
      toast.success(parts.join(" · ") || "Done");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-lg" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">ASSIGN {program.name?.toUpperCase()}</DialogTitle>
          <DialogDescription className="text-xs text-text-secondary">Pick one or more clients and/or teams. Assignments already active for a client are skipped.</DialogDescription>
        </DialogHeader>
        {clients.length === 0 && (teams || []).length === 0 ? (
          <p className="text-sm text-text-secondary">No clients or teams yet. Add clients or create a team first.</p>
        ) : (
          <div className="space-y-4">
            {clients.length > 0 && (
              <div>
                <label className="label-tiny block mb-2">CLIENTS ({selectedClients.length}/{clients.length})</label>
                <div className="max-h-40 overflow-y-auto border border-white/10 divide-y divide-white/10" data-testid="assign-client-list">
                  {clients.map((c) => (
                    <label key={c.client.user_id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/[0.03] cursor-pointer" data-testid={`assign-client-${c.client.user_id}`}>
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(c.client.user_id)}
                        onChange={() => toggle(selectedClients, setSelectedClients, c.client.user_id)}
                      />
                      <span>{c.client.name}</span>
                      <span className="text-xs text-text-secondary ml-auto">{c.client.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(teams || []).length > 0 && (
              <div>
                <label className="label-tiny block mb-2">TEAMS ({selectedTeams.length}/{teams.length})</label>
                <div className="max-h-32 overflow-y-auto border border-white/10 divide-y divide-white/10" data-testid="assign-team-list">
                  {teams.map((t) => (
                    <label key={t.team_id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/[0.03] cursor-pointer" data-testid={`assign-team-${t.team_id}`}>
                      <input
                        type="checkbox"
                        checked={selectedTeams.includes(t.team_id)}
                        onChange={() => toggle(selectedTeams, setSelectedTeams, t.team_id)}
                      />
                      <UsersThree size={12} weight="duotone" className="!text-yellow-400" />
                      <span>{t.name}</span>
                      <span className="text-xs text-text-secondary ml-auto">{t.member_count || 0} members</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="label-tiny block mb-2">START DATE</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-surface-elevated border-white/10"
                data-testid="assign-date-input"
              />
            </div>
            <button
              onClick={assign}
              disabled={assigning || (selectedClients.length === 0 && selectedTeams.length === 0)}
              className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50"
              data-testid="confirm-assign-button"
            >
              {assigning ? "ASSIGNING..." : `CONFIRM · ${selectedClients.length} CLIENT${selectedClients.length === 1 ? "" : "S"} + ${selectedTeams.length} TEAM${selectedTeams.length === 1 ? "" : "S"}`}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CopyProgramDialog({ program, allPrograms, onClose, onCopied }) {
  const [scope, setScope] = useState("session");
  const [targetProgramId, setTargetProgramId] = useState(allPrograms[0]?.program_id || "");
  const [sourceWeek, setSourceWeek] = useState(1);
  const [sourceDay, setSourceDay] = useState(1);
  const [sourceExerciseIndex, setSourceExerciseIndex] = useState(0);
  const [targetWeek, setTargetWeek] = useState(1);
  const [targetDay, setTargetDay] = useState(1);
  const [weekFrom, setWeekFrom] = useState(1);
  const [weekTo, setWeekTo] = useState(1);
  const [weekStart, setWeekStart] = useState(1);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!targetProgramId) return toast.error("Pick a target program");
    setBusy(true);
    try {
      const body = { source_program_id: program.program_id, target_program_id: targetProgramId, scope, source: {}, target: {} };
      if (scope === "exercise") {
        body.source = { week: +sourceWeek, day: +sourceDay, exercise_index: +sourceExerciseIndex };
        body.target = { week: +targetWeek, day: +targetDay };
      } else if (scope === "session") {
        body.source = { week: +sourceWeek, day: +sourceDay };
        body.target = { week: +targetWeek, day: +targetDay };
      } else if (scope === "week") {
        body.source = { week: +sourceWeek };
        body.target = { week: +targetWeek };
      } else {
        body.source = { week_from: +weekFrom, week_to: +weekTo };
        body.target = { week_start: +weekStart };
      }
      const { data } = await api.post("/coach/programs/copy", body);
      toast.success(`${data.copied} ${scope}${data.copied === 1 ? "" : "s"} copied`);
      onCopied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-lg" data-testid="copy-program-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">COPY FROM {program.name?.toUpperCase()}</DialogTitle>
          <DialogDescription className="text-xs text-text-secondary">Copy a single exercise, session, week, or block into another program.</DialogDescription>
        </DialogHeader>
        {allPrograms.length === 0 ? (
          <p className="text-sm text-text-secondary">You need at least one other program to copy into. Create or duplicate a program first.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="label-tiny block mb-2">SCOPE</label>
              <div className="grid grid-cols-4 gap-1" data-testid="copy-scope-selector">
                {["exercise", "session", "week", "block"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScope(s)}
                    className={`text-xs label-tiny py-2 border ${scope === s ? "!bg-yellow-400 text-slate-900 !border-yellow-400" : "border-white/10 text-text-secondary hover:border-white/30"}`}
                    data-testid={`copy-scope-${s}`}
                  >
                    {s.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label-tiny block mb-2">TARGET PROGRAM</label>
              <select
                value={targetProgramId}
                onChange={(e) => setTargetProgramId(e.target.value)}
                className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm"
                data-testid="copy-target-program"
              >
                {allPrograms.map((p) => <option key={p.program_id} value={p.program_id}>{p.name}</option>)}
              </select>
            </div>

            {scope === "exercise" && (
              <div className="grid grid-cols-2 gap-3">
                <NumField label="SOURCE WEEK" value={sourceWeek} onChange={setSourceWeek} testid="copy-src-week" />
                <NumField label="SOURCE DAY" value={sourceDay} onChange={setSourceDay} testid="copy-src-day" />
                <NumField label="EXERCISE INDEX (0-based)" value={sourceExerciseIndex} onChange={setSourceExerciseIndex} testid="copy-src-ex-idx" />
                <div />
                <NumField label="TARGET WEEK" value={targetWeek} onChange={setTargetWeek} testid="copy-tgt-week" />
                <NumField label="TARGET DAY" value={targetDay} onChange={setTargetDay} testid="copy-tgt-day" />
              </div>
            )}

            {scope === "session" && (
              <div className="grid grid-cols-2 gap-3">
                <NumField label="SOURCE WEEK" value={sourceWeek} onChange={setSourceWeek} testid="copy-src-week" />
                <NumField label="SOURCE DAY" value={sourceDay} onChange={setSourceDay} testid="copy-src-day" />
                <NumField label="TARGET WEEK" value={targetWeek} onChange={setTargetWeek} testid="copy-tgt-week" />
                <NumField label="TARGET DAY" value={targetDay} onChange={setTargetDay} testid="copy-tgt-day" />
              </div>
            )}

            {scope === "week" && (
              <div className="grid grid-cols-2 gap-3">
                <NumField label="SOURCE WEEK" value={sourceWeek} onChange={setSourceWeek} testid="copy-src-week" />
                <NumField label="TARGET WEEK (WILL OVERWRITE)" value={targetWeek} onChange={setTargetWeek} testid="copy-tgt-week" />
              </div>
            )}

            {scope === "block" && (
              <div className="grid grid-cols-3 gap-3">
                <NumField label="WEEK FROM" value={weekFrom} onChange={setWeekFrom} testid="copy-block-from" />
                <NumField label="WEEK TO" value={weekTo} onChange={setWeekTo} testid="copy-block-to" />
                <NumField label="TARGET WEEK START" value={weekStart} onChange={setWeekStart} testid="copy-block-start" />
              </div>
            )}

            <button
              onClick={submit}
              disabled={busy}
              className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50"
              data-testid="confirm-copy-button"
            >
              {busy ? "COPYING..." : `COPY ${scope.toUpperCase()}`}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-tiny text-[9px] text-text-secondary block mb-1">{label}</label>
      <Input type="number" min="1" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="bg-surface-elevated border-white/10 text-sm" />
    </div>
  );
}

/* ------------ Share Program Dialog ------------ */

function ShareProgramDialog({ program, onClose }) {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/coach/programs/${program.program_id}/share`)
      .then(({ data }) => { if (!cancelled) setShare(data); })
      .catch(() => { if (!cancelled) setShare(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [program.program_id]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/coach/programs/${program.program_id}/share`);
      setShare(data);
      toast.success(share ? "Share code rotated" : "Share code created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate code");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this share code? Coaches who saved it will no longer be able to import.")) return;
    setBusy(true);
    try {
      await api.delete(`/coach/programs/${program.program_id}/share`);
      setShare(null);
      toast.success("Share code revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    } finally { setBusy(false); }
  };

  const copyCode = async () => {
    if (!share?.share_code) return;
    try {
      await navigator.clipboard.writeText(share.share_code);
      toast.success("Code copied");
    } catch {
      toast.error("Copy failed — long-press to select");
    }
  };

  const shareUrl = share?.share_code ? `${window.location.origin}/coach/import/${share.share_code}` : "";

  const copyUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — send it to any coach");
    } catch {
      toast.error("Copy failed — long-press to select");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/20 text-white max-w-lg" data-testid="share-program-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">SHARE PROGRAM</DialogTitle>
          <DialogDescription className="text-xs text-white/70">
            Generate a one-off code so another Coach Pro can import <span className="!text-yellow-400">{program.name}</span> into their library.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-sm text-white/60" data-testid="share-loading">Loading…</div>
        ) : share ? (
          <div className="space-y-4">
            <div>
              <label className="label-tiny text-[11px] text-white/70 block mb-2">ACTIVE SHARE CODE</label>
              <div className="flex items-center gap-2">
                <div
                  className="flex-1 font-heading text-3xl tracking-[0.3em] !text-yellow-400 bg-surface-elevated border !border-yellow-400/40 px-4 py-3 text-center select-all"
                  data-testid="share-code-value"
                >
                  {share.share_code}
                </div>
                <button
                  onClick={copyCode}
                  className="border border-white/20 hover:!border-yellow-400 hover:!text-yellow-400 px-3 py-3 text-xs label-tiny tracking-widest transition-colors"
                  data-testid="share-copy-button"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-[11px] text-white/60 mt-2" data-testid="share-uses">
                Used {share.use_count} time{share.use_count === 1 ? "" : "s"} · created {new Date(share.created_at).toLocaleDateString()}
              </div>
            </div>

            <div>
              <label className="label-tiny text-[11px] text-white/70 block mb-2">ONE-TAP IMPORT LINK</label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  className="flex-1 font-mono text-xs bg-surface-elevated border border-white/15 px-3 py-2.5 text-slate-800 select-all"
                  data-testid="share-url-value"
                />
                <button
                  onClick={copyUrl}
                  className="border border-white/20 hover:!border-yellow-400 hover:!text-yellow-400 px-3 py-2.5 text-xs label-tiny tracking-widest transition-colors"
                  data-testid="share-copy-url-button"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-[11px] text-white/60 mt-1.5">
                Any Coach Pro who opens this URL will see a preview and can import the program in one tap.
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={generate}
                disabled={busy}
                className="flex-1 border border-white/25 hover:!border-yellow-400 hover:!text-yellow-400 py-3 text-xs label-tiny tracking-widest transition-colors disabled:opacity-50"
                data-testid="share-rotate-button"
              >
                {busy ? "…" : "ROTATE CODE"}
              </button>
              <button
                onClick={revoke}
                disabled={busy}
                className="flex-1 border border-red-400/40 hover:!border-red-400 hover:!text-red-400 text-red-300 py-3 text-xs label-tiny tracking-widest transition-colors disabled:opacity-50"
                data-testid="share-revoke-button"
              >
                REVOKE
              </button>
            </div>
            <p className="text-[11px] text-white/60 italic">Rotating invalidates the old code. Revoking stops all future imports; existing imported copies remain with the recipient.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-white/80">No active share code for this program yet.</p>
            <button
              onClick={generate}
              disabled={busy}
              className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50"
              data-testid="share-generate-button"
            >
              {busy ? "GENERATING…" : "GENERATE SHARE CODE"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------ Import Program Dialog ------------ */

function ImportProgramDialog({ onClose, onImported }) {
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");

  const doPreview = async () => {
    const c = code.trim().toUpperCase();
    if (c.length < 4) { setError("Enter a share code"); return; }
    setError(""); setLoading(true); setPreview(null);
    try {
      const { data } = await api.get(`/coach/programs/shared/${c}`);
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid or expired share code");
    } finally { setLoading(false); }
  };

  const doImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/coach/programs/import/${preview.share_code}`);
      toast.success(`Imported "${data.program.name}"`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/20 text-white max-w-lg" data-testid="import-program-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">IMPORT PROGRAM</DialogTitle>
          <DialogDescription className="text-xs text-white/70">
            Paste an 8-character share code to preview and clone a program from another coach into your library.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="label-tiny text-[11px] text-white/70 block mb-2">SHARE CODE</label>
            <div className="flex items-center gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. HGF3DV3Q"
                maxLength={8}
                className="bg-surface-elevated border-white/25 !text-yellow-400 font-heading text-2xl tracking-[0.3em] text-center h-14"
                data-testid="import-code-input"
                autoFocus
              />
              <button
                onClick={doPreview}
                disabled={loading || code.trim().length < 4}
                className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors px-4 py-4 text-xs label-tiny tracking-widest disabled:opacity-50"
                data-testid="import-preview-button"
              >
                {loading ? "…" : "PREVIEW"}
              </button>
            </div>
            {error && <div className="mt-2 text-xs text-red-400" data-testid="import-error">{error}</div>}
          </div>
          {preview && (
            <div className="border border-white/15 bg-surface-elevated/60 p-4 space-y-2" data-testid="import-preview-card">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="label-tiny !text-yellow-400 text-[10px]">// SHARED BY</div>
                  <div className="text-sm text-white font-bold">{preview.author.name}</div>
                  {preview.author.specialty && <div className="text-[11px] text-white/60">{preview.author.specialty}</div>}
                </div>
                {preview.is_own && (
                  <span className="text-[10px] label-tiny tracking-wider px-2 py-1 border border-red-400/40 !text-red-300">YOUR OWN CODE</span>
                )}
              </div>
              <div className="pt-2 border-t border-white/10">
                <div className="font-heading text-xl text-white">{preview.program.name}</div>
                {preview.program.description && <div className="text-[12px] text-white/70 mt-1 line-clamp-3">{preview.program.description}</div>}
                <div className="flex flex-wrap gap-3 label-tiny text-[11px] mt-3">
                  <span className="!text-yellow-400">{preview.program.weeks}W</span>
                  <span>{preview.program.day_count} SESSIONS</span>
                  <span>{preview.program.exercise_count} EX</span>
                  {preview.program.goal && <span className="text-white/70">· {preview.program.goal}</span>}
                </div>
              </div>
              <button
                onClick={doImport}
                disabled={importing || preview.is_own}
                className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3 text-xs label-tiny tracking-widest mt-1 disabled:opacity-50"
                data-testid="import-confirm-button"
              >
                {importing ? "IMPORTING…" : preview.is_own ? "CANNOT IMPORT YOUR OWN CODE" : `IMPORT INTO MY LIBRARY`}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}



